import { useRef, type ComponentType, type KeyboardEvent } from 'react'
import { cn } from '@/shared/lib/utils'

export interface SegmentedOption<T extends string> {
  value: T
  label: string
  icon?: ComponentType<{ className?: string }>
}

/**
 * Contrôle segmenté accessible (radiogroup) - base des toggles thème/densité.
 * Tous les libellés sont fournis i18n par l'appelant.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  className,
}: {
  options: SegmentedOption<T>[]
  value: T
  onChange: (next: T) => void
  ariaLabel: string
  className?: string
}) {
  const buttonsRef = useRef<(HTMLButtonElement | null)[]>([])

  // Pattern radiogroup WAI-ARIA : flèches déplacent la sélection + le focus (roving tabindex).
  const moveTo = (index: number): void => {
    const count = options.length
    const next = ((index % count) + count) % count
    const option = options[next]
    if (option === undefined) {
      return
    }
    onChange(option.value)
    buttonsRef.current[next]?.focus()
  }

  const onKeyDown = (event: KeyboardEvent, currentIndex: number): void => {
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault()
        moveTo(currentIndex + 1)
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault()
        moveTo(currentIndex - 1)
        break
      case 'Home':
        event.preventDefault()
        moveTo(0)
        break
      case 'End':
        event.preventDefault()
        moveTo(options.length - 1)
        break
      default:
        break
    }
  }

  const selectedIndex = options.findIndex((o) => o.value === value)

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex items-center gap-0.5 rounded-pill border border-[var(--v2-line)] bg-[var(--v2-bg-soft)] p-0.5',
        className,
      )}
    >
      {options.map((option, index) => {
        const selected = option.value === value
        const Icon = option.icon
        return (
          <button
            key={option.value}
            ref={(el) => {
              buttonsRef.current[index] = el
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            // Un seul élément tabbable (le sélectionné, ou le 1er si aucun) : roving tabindex.
            tabIndex={selected || (selectedIndex === -1 && index === 0) ? 0 : -1}
            onClick={() => onChange(option.value)}
            onKeyDown={(e) => onKeyDown(e, index)}
            className={cn(
              'inline-flex min-h-8 items-center gap-1.5 rounded-pill px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              selected
                ? 'bg-[var(--v2-surface)] text-[var(--v2-ink)] shadow-sm'
                : 'text-[var(--v2-ink-3)] hover:text-[var(--v2-ink)]',
            )}
          >
            {Icon ? <Icon className="size-4 shrink-0" aria-hidden /> : null}
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
